import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  School, BookOpen, Users, Target, ScrollText, Database,
  Crown, Shield, ChevronRight, ArrowRight,
} from 'lucide-react';

// Accesos del profesor — mismas rutas y permisos que menus.teacher en AppLayout
// (fuente de verdad del sidebar). No introduce rutas ni permisos nuevos.
const TOOLS = [
  { key: 'salones',    to: '/teacher/classrooms', label: 'Salones',       desc: 'Clases en vivo con tus alumnos',     icon: School,        tile: 'bg-amber-100 text-amber-700',   glow: 'group-hover:shadow-amber-200/60' },
  { key: 'studies',    to: '/teacher/studies',    label: 'Estudios',      desc: 'Crea y organiza estudios',            icon: BookOpen,      tile: 'bg-blue-100 text-blue-700',     glow: 'group-hover:shadow-blue-200/60' },
  { key: 'grupos',     to: '/teacher/groups',     label: 'Grupos',        desc: 'Gestiona grupos y estudiantes',       icon: Users,         tile: 'bg-indigo-100 text-indigo-700', glow: 'group-hover:shadow-indigo-200/60' },
  { key: 'quizzes',    to: '/teacher/quizzes',    label: 'Quizzes',       desc: 'Evaluaciones interactivas',           icon: Target,        tile: 'bg-amber-100 text-amber-700',   glow: 'group-hover:shadow-amber-200/60' },
  { key: 'recaps',     to: '/teacher/recaps',     label: 'Recaps',        desc: 'Resúmenes de tus clases',             icon: ScrollText,    tile: 'bg-violet-100 text-violet-700', glow: 'group-hover:shadow-violet-200/60' },
  { key: 'database',   to: '/teacher/database',   label: 'Base de datos', desc: 'Partidas y posiciones PGN',           icon: Database,      tile: 'bg-slate-200 text-slate-700',   glow: 'group-hover:shadow-slate-300/60' },
];

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    api.get('/groups').then(r => setGroups(r.data)).catch(() => {});
  }, []);

  const enter = (i) => ({
    transitionDelay: `${i * 45}ms`,
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(12px)',
  });

  return (
    <div className="min-h-full bg-[#F5F7FB]" data-testid="teacher-dashboard">
      {/* HEADER — banner Chess4Kings */}
      <div
        className="relative aspect-[5/1] overflow-hidden bg-[#002537] bg-[length:100%_auto] bg-[center_70%] bg-no-repeat text-white"
        style={!user?.is_owner ? {
          backgroundImage:
            "url(/branding/banner-chess4kings-teal.png)",
        } : undefined}
      >
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-10 sm:pt-10 sm:pb-14 flex items-center">
          <div className="relative z-10 max-w-xl">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-xl bg-white/10 backdrop-blur grid place-items-center ring-1 ring-white/15">
                <Crown className="w-5 h-5 text-amber-300" />
              </div>
              <span className="font-display font-extrabold tracking-tight">
                Chess4Kings
              </span>

              {user?.is_owner && (
                <span
                  data-testid="owner-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-white text-[9px] font-extrabold uppercase tracking-widest"
                >
                  <Shield className="w-2.5 h-2.5" /> Owner
                </span>
              )}
            </div>

            <h1
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight drop-shadow-lg"
              data-testid="teacher-greeting"
            >
              ¡Hola, {user?.first_name || 'profesor'}!{' '}
              <span className="text-amber-400">♞</span>
            </h1>

            <p className="text-white/90 mt-3 text-sm sm:text-base font-medium drop-shadow-md">
              Bienvenido nuevamente a Chess4Kings.
            </p>

            <div className="flex flex-wrap gap-2 mt-5">
              <span className="inline-flex items-center gap-1.5 text-xs text-white/85 bg-[#002537]/45 backdrop-blur-sm ring-1 ring-white/15 rounded-full px-3 py-1.5">
                <Users className="w-3.5 h-3.5 text-amber-300" /> {groups.length} {groups.length === 1 ? "grupo" : "grupos"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-12 relative">
        {/* ACCESOS PRINCIPALES — protagonistas */}
        <div className="rounded-t-3xl bg-white px-5 sm:px-6 pt-5 pb-4 -mx-2 sm:-mx-3 mb-1">
          <h2 className="font-display font-bold text-lg text-slate-800">Herramientas</h2>
          <p className="text-sm text-slate-500 mt-1">Selecciona una herramienta para continuar</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {TOOLS.map((t, i) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.key}
                to={t.to}
                data-testid={`teacher-tool-${t.key}`}
                style={enter(i)}
                className={`group relative flex flex-col rounded-2xl bg-white border border-slate-200 p-3 sm:p-4 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl ${t.glow} hover:border-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 overflow-hidden`}
              >
                <>
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-300" />
                  <Crown className="absolute -right-1 -bottom-2 w-20 h-20 text-amber-500 opacity-[0.035] pointer-events-none" />
                </>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl grid place-items-center mb-2 sm:mb-3 transition-transform duration-300 group-hover:scale-105 ${t.tile}`}>
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="font-display font-bold text-base sm:text-lg text-slate-800 leading-tight">{t.label}</div>
                <div className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-snug">{t.desc}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-600 group-hover:text-amber-700 transition-colors">
                  Abrir <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* ACTIVIDAD — secundario */}
        <div className="grid grid-cols-1 gap-4 sm:gap-5 mt-6 sm:mt-8">
          <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-200 p-5 sm:p-6" data-testid="teacher-groups-panel">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-300" />
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-base sm:text-lg text-slate-800">Mis grupos</h3>
              <Link to="/teacher/groups" className="text-xs font-semibold text-blue-600 inline-flex items-center gap-0.5 hover:text-blue-700">
                Ver todos <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            {groups.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">Aún no tienes grupos.</div>
            ) : groups.slice(0, 4).map(g => (
              <Link key={g.id} to="/teacher/groups" className="flex items-center gap-3 py-2.5 border-b last:border-0 border-slate-100 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 grid place-items-center shrink-0"><Users className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-800 truncate">{g.name}</div>
                  <div className="text-xs text-slate-400 truncate">{g.description || 'Grupo'}</div>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
